import type { ArgsOf, RequestOf } from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";

import { commonAllAccess } from "./all.ts";
import { commonSshAccess } from "./group.ts";
import {
  aliasParameter,
  groupDestinationParameter,
  groupNameParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";

const parentLabel = "AWS account";

const awsStagedResource = {
  type: "object",
  label: "Generated resource data",
  properties: {
    arn: {
      type: "string",
      label: "Resource ARN",
      description: "The ARN of the resource",
    },
    name: {
      type: "string",
      label: "Role name",
    },
    policyArn: {
      type: "string",
      label: "Policy ARN",
    },
    policyName: {
      type: "string",
      label: "Policy name",
    },
    policyDocument: {
      type: "string",
      label: "Policy document",
    },
  },
} as const;

const awsResource = {
  type: "object",
  label: "Resource data",
  properties: {
    account: {
      type: "generated",
      label: "Account",
    },
    instanceId: {
      type: "generated",
      label: "Instance ID",
    },
    instanceName: {
      type: "generated",
      label: "Instance name",
    },
    arn: {
      type: "generated",
      label: "Resource ARN",
      description: "The ARN of the resource",
    },
    name: {
      type: "generated",
      label: "Resource name",
      description: "The name of the resource",
    },
    object: {
      type: "generated",
      label: "Object",
      optional: true,
    },
    policies: {
      type: "generated",
      label: "Policies",
      multivalued: true,
    },
    extends: {
      type: "generated",
      label: "Extends",
      optional: true,
    },
    extendsName: {
      type: "generated",
      label: "Extend policies",
      optional: true,
    },
    service: {
      type: "generated",
      label: "Service",
    },
    accountId: {
      type: "generated",
      label: "Account ID",
    },
    idcArn: {
      type: "generated",
      label: "IDC ARN",
      optional: true,
    },
    idcId: {
      type: "generated",
      label: "IDC ID",
      optional: true,
    },
    idcRegion: {
      type: "generated",
      label: "IDC region",
      optional: true,
    },
    region: {
      type: "generated",
      label: "Region",
    },
    userName: {
      type: "generated",
      label: "User name",
    },
    resourceTag: {
      type: "object",
      label: "Resource tag",
      optional: true,
      properties: {
        key: {
          type: "generated",
          label: "Key",
        },
        value: {
          type: "generated",
          label: "Value",
        },
      },
    },
  },
} as const;

export const AwsSshAccesses = {
  session: Access({
    ...commonSshAccess,
    maturity: "ga",
    delegation: {},
    generated: {
      linuxUserName: { type: "string" },
      publicKey: { type: "string", optional: true },
      accessFrom: {
        type: "string",
        choices: ["group", "session", "all", "parent"] as const,
      },
      ssmAccessProvisionDocument: { type: "string" },
      hostKeys: {
        type: "string",
        multivalued: true,
        optional: true,
      },
      resource: {
        type: "object",
        properties: {
          arn: { type: "string" },
          name: { type: "string" },
          policyArn: { type: "string" },
          policyName: { type: "string" },
          policyDocument: { type: "string" },
        },
      },
    }, // depends on the ssh access subtype
    parameters: {
      provider: { ...requiredProviderParameter, choices: ["aws"] as const },
      parent: parentParameter(parentLabel),
      group: groupNameParameter,
      publicKey: publicKeyParameter,
      region: regionParameter,
      destination: sessionDestinationParameter,
      sudo: sudoParameter,
      zone: zoneParameter,
      resource: awsResource,
    },
    render: {
      locator: "parent",
      policy: "sudo",
      resource: "destination",
    } as const,
  }),
  group: Access({
    description: "Group of instances",
    label: "SSH group",
    maturity: "ga",
    delegation: {},
    generated: {
      name: {
        type: "string",
      },
      resource: awsStagedResource,
    },
    parameters: {
      provider: { ...requiredProviderParameter, optional: true },
      parent: parentParameter(parentLabel),
      name: groupNameParameter,
      destination: groupDestinationParameter,
      sudo: sudoParameter,
      alias: aliasParameter,
      key: {
        type: "generated",
        label: "Group key",
      },
      resource: awsResource,
    },
    render: { locator: "parent", resource: "name", policy: "sudo" } as const,
  }),
  all: commonAllAccess,
  parent: {
    ...commonParentAccess(parentLabel),
    generated: {
      resource: awsStagedResource,
    },
    parameters: {
      provider: { ...requiredProviderParameter, choices: ["aws"] } as const,
      parent: {
        ...parentParameter(parentLabel),
        label: parentLabel,
        optional: undefined,
      } as const,
      sudo: sudoParameter,
      alias: aliasParameter,
      resource: awsResource,
    },
    render: { locator: "parent", policy: "sudo" } as const,
  },
} satisfies EmptyAccesses;

export type AwsSshAccesses = typeof AwsSshAccesses;
export type AwsSshAccess = AwsSshAccesses[keyof AwsSshAccesses];
export type AwsSshAccessArgs = ArgsOf<AwsSshAccess>;

export type AwsSshSessionAccess = AwsSshAccesses["session"];
export type AwsSshSessionAccessArgs = ArgsOf<AwsSshSessionAccess>;
export type AwsSshSessionRequest = RequestOf<AwsSshSessionAccess>;

export type AwsSshGroupAccess = AwsSshAccesses["group"];
export type AwsSshGroupAccessArgs = ArgsOf<AwsSshGroupAccess>;

export type AwsSshParentAccess = AwsSshAccesses["parent"];
export type AwsSshParentAccessArgs = ArgsOf<AwsSshParentAccess>;

export type AwsSshAllAccess = AwsSshAccesses["all"];
export type AwsSshAllAccessArgs = ArgsOf<AwsSshAllAccess>;
